import { Accordion, AccordionItem, Modal } from "@appkit4/react-components";
import React, { useEffect, useMemo, useState } from "react";
import DataImportModal from "../../components/DataImportModal/DataImportModal";
import toast from "../../components/DismissibleToast";
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import {
  setANGIETotals,
  setQNGIETotals,
} from "../../services/calculations/GroupDataCalcs";
import GroupDataTable from "./GroupDataTable";
import {
  WarningModal,
  handleCancel,
  renderPrimaryAccordionTitle,
  renderSummary,
  sumArray,
} from "./groupDataUtil";

import { getPageTooltips } from "../../services/GroupContext";
import { getAngieRowConfig } from "./utils/angieUtils";
import { useGroupDataRows } from "./utils/groupDataUtil";

const ANGIE = ({
  setVisible,
  currentPeriod,
  angieEstimated,
  setAngieEstimated,
  setCurrentPeriod,
  save,
  errorState: [isError, setIsError],
  isParentError,
  isReadOnly,
}) => {
  const tooltips = useMemo(() => getPageTooltips("angieModal"), []);
  const angieRowConfig = useMemo(() => getAngieRowConfig(tooltips), [tooltips]);
  const tables = useGroupDataRows(angieRowConfig);

  const upwardAdjustments = [
    tables["capitalised_expense_in_statements"]?.state[0],
    tables["capitalised_income_written_off"]?.state[0],
    tables["expense_amount_recognised_in_equity_or_shareholders"]?.state[0],
    tables["upward_amount_under_s330za"]?.state[0],
    tables["post_2020_transitional_amounts_s607za_cta2009"]?.state[0],
    tables["post_2020_income_amounts_s330za_transitional"]?.state[0],
    tables["post_2020_derivative_income_s607za"]?.state[0],
    tables["upward_amount_under_s330_cta_election"]?.state[0],
    tables["relevant_debt_release_tax_exempt_income"]?.state[0],
  ];
  const [upwardAdjustmentsTotal, setUpwardAdjustmentsTotal] = useState(0);

  const downwardAdjustments = [
    tables["capitalised_income_in_statements"]?.state[0],
    tables["capitalised_expense_written_off"]?.state[0],
    tables["income_amount_recognised_in_equity_or_shareholders"]?.state[0],
    tables["downward_amount_under_s330ZA"]?.state[0],
    tables["post_2020_derivative_expense_s607za"]?.state[0],
    tables["downward_amount_under_s330_cta_election"]?.state[0],
    tables["expense_amount_excluded_for_s322_2_or_cta2009"]?.state[0],
    tables["expense_amount_for_dividends_on_preference_shares"]?.state[0],
  ];
  const [downwardAdjustmentsTotal, setDownwardAdjustmentsTotal] = useState(0);

  const mismatchAdjustments = [
    tables["mismatches_regulation_2_adjustments"]?.state[0],
    tables["mismatches_regulation_3_adjustments"]?.state[0],
    tables["mismatches_regulation_4_adjustments"]?.state[0],
  ];
  const [mismatchAdjustmentsTotal, setMismatchAdjustmentsTotal] = useState(0);

  const interestAllowanceAltCalcElection = [
    tables["capitalised_upward_interest_adjustment"]?.state[0],
    tables["capitalised_downward_interest_adjustment"]?.state[0],
    tables["reversal_gaap_tax_assets_s423_2_a"]?.state[0],
    tables["gaap_assets_written_off_s423_2_b"]?.state[0],
    tables["gaap_relevant_expense_written_off_s423_2a"]?.state[0],
    tables["gaap_relevant_income_written_off_s423_2b"]?.state[0],
    tables["gaap_asset_carrying_value_relevant_expense_s423_2aa_a"]?.state[0],
    tables["gaap_asset_carrying_value_relevant_income_s423_2aa_b"]?.state[0],
  ];
  const [interestAltCalcTotal, setInterestAltCalcTotal] = useState(0);

  const otherAdjustments = [
    tables["specialist_regimes_adjustments"]?.state[0],
    tables["miscellaneous_adjustments"]?.state[0],
  ];
  const [otherAdjustmentsTotal, setOtherAdjustments] = useState(0);

  const adjustmentsForQualifyingInfraCompanies = [
    tables["exclude_qic_income_exempt_s440_s442_2a"]?.state[0],
    tables["exclude_qic_expense_exempt_s438_s442_2a"]?.state[0],
  ];
  const [infraAdjustmentsTotal, setInfraAdjustmentsTotal] = useState(0);

  const [totalAdjustments, setTotalAdjustments] = useState(0);
  const [totalWithoutNGIE, setTotalWithoutNGIE] = useState(0);
  const [ngieETotal, setNgieTotal] = useState(
    currentPeriod?.local_curr_net_group_interest_expense
      ? currentPeriod?.local_curr_net_group_interest_expense
      : 0,
  );
  const [angieAmountBeforeAnyAdjustments, setAngieAmountBeforeAnyAdjustments] =
    useState(0);

  useEffect(() => {
    setIsError(Object.keys(tables)?.some((k) => tables[k]?.error[0]));
  }, [Object.keys(tables)?.map((k) => tables[k]?.error[0])]);

  const [activeKeys, setActiveKeys] = useState("");
  const onClickAccordion = (activeKeys) => {
    setActiveKeys(activeKeys);
  };
  useEffect(() => {
    let total = sumArray(upwardAdjustments);
    setUpwardAdjustmentsTotal(total);
  }, upwardAdjustments);

  useEffect(() => {
    let total = sumArray(downwardAdjustments);
    setDownwardAdjustmentsTotal(total === 0 ? 0 : total);
  }, downwardAdjustments);

  useEffect(() => {
    let total = sumArray(mismatchAdjustments);
    setMismatchAdjustmentsTotal(total);
  }, [mismatchAdjustments]);

  useEffect(() => {
    setInterestAltCalcTotal(sumArray(interestAllowanceAltCalcElection));
  }, [interestAllowanceAltCalcElection]);

  useEffect(() => {
    setOtherAdjustments(sumArray(otherAdjustments));
  }, [otherAdjustments]);

  useEffect(() => {
    setInfraAdjustmentsTotal(sumArray(adjustmentsForQualifyingInfraCompanies));
  }, [adjustmentsForQualifyingInfraCompanies]);

  useEffect(() => {
    let total =
      upwardAdjustmentsTotal +
      downwardAdjustmentsTotal +
      mismatchAdjustmentsTotal +
      interestAltCalcTotal +
      otherAdjustmentsTotal +
      sumArray([
        tables["angie_proportion_associated_groups_s427_4"]?.state[0],
      ]) +
      infraAdjustmentsTotal;
    let totalBeforeAnyAdjustments =
      upwardAdjustmentsTotal +
      downwardAdjustmentsTotal +
      mismatchAdjustmentsTotal +
      interestAltCalcTotal +
      otherAdjustmentsTotal;
    setTotalWithoutNGIE(totalBeforeAnyAdjustments);
    if (currentPeriod?.local_curr_net_group_interest_expense) {
      total += currentPeriod?.local_curr_net_group_interest_expense;
      totalBeforeAnyAdjustments +=
        currentPeriod?.local_curr_net_group_interest_expense;
    }

    setTotalAdjustments(Math.round(total));
    setAngieAmountBeforeAnyAdjustments(Math.round(totalBeforeAnyAdjustments));
  }, [
    upwardAdjustmentsTotal,
    downwardAdjustmentsTotal,
    mismatchAdjustmentsTotal,
    interestAltCalcTotal,
    otherAdjustmentsTotal,
    ,
    tables["angie_proportion_associated_groups_s427_4"].state[0],
    infraAdjustmentsTotal,
  ]);

  useEffect(() => {
    const angie = currentPeriod?.angie;
    if (!angie) {
      return;
    }

    Object.keys(tables)?.forEach((key) => {
      tables[key]?.state[1](angie[key]);
    });
  }, []);

  const updateAngieValues = (angie) => {
    const newPeriod = {
      ...currentPeriod,
      angie,
    };

    setANGIETotals(newPeriod);

    if (newPeriod?.qngie) {
      setQNGIETotals(newPeriod);
    }

    return newPeriod;
  };

  const submit = () => {
    const angie = Object.keys(tables)?.reduce((acc, k) => {
      acc[k] = tables[k]?.state[0];
      return acc;
    }, {});

    const newPeriod = updateAngieValues(angie);

    setCurrentPeriod(newPeriod);
    const promise = save(newPeriod);
    toast.promise(promise, {
      loading: "Saving NGIE data...",
      success: () => {
        setVisible(false);
        return "Saved ANGIE data";
      },
      error: "Something went wrong with saving ANGIE data:",
    });
  };
  const [isModalChanged, setIsModalChanged] = useState(false);

  useEffect(() => {
    const total = totalAdjustments;
    if (
      currentPeriod?.local_curr_adjusted_net_group_interest_expense !== total
    ) {
      setIsModalChanged(true);
    } else {
      setIsModalChanged(false);
    }
  }, [totalAdjustments]);

  const [showWarningModal, setShowWarningModal] = useState(false);

  const onImportSuccess = (angie) => {
    const isPlainObject = (val) =>
      typeof val === "object" && val !== null && val.constructor === Object;

    if (isPlainObject(angie)) {
      updateAngieValues(angie);
      Object.entries(tables)?.forEach(([key, row]) => {
        row.state[1](angie[key]);
      });
    } else {
      console.error("error setting values");
      toast.error("Error Importing ANGIE");
    }
  };

  const handleExitWithoutSaving = () => {
    setIsError(false);
    setVisible(false);
  };

  return (
    <>
      <Modal
        initialFocus={false}
        visible={true}
        title={`Adjusted net group-interest expense`}
        onCancel={() =>
          handleCancel(
            isModalChanged,
            isReadOnly,
            setVisible,
            setShowWarningModal,
            setIsError,
          )
        }
        maskCloseable={false}
        modalStyle={{ width: "90%", maxHeight: "90%" }}
        bodyStyle={{ maxHeight: "90%" }}
      >
        <table className="table">
          <tbody>
            <tr>
              <td>
                <div className="row">
                  <p className="col-1">Selected input value currency:</p>
                  <Input
                    className="col"
                    disabled
                    value={currentPeriod?.currency}
                  />
                </div>
              </td>
              <td>
                <div className="row">
                  <p className="col-4">
                    Entered FX rate: 1 GBP = X Local Currency (if required):
                  </p>
                  <InputNumber
                    className="col"
                    value={currentPeriod?.fx_rate}
                    disabled
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: "flex", gap: "0.5rem", paddingLeft: "5px" }}>
          <p>
            Use of estimates: Tick this box to indicate that estimated amounts
            are used in the calculation of the Adjusted net group-interest
            expense
          </p>
          <Checkbox
            hiddenInReadonly
            onChange={(val) => setAngieEstimated(val)}
            checked={angieEstimated}
          />
        </div>

        <div className="row group-data-modal-top-input-header">
          <p>
            Changes to the currency and exchange rate can be made in the Net
            group-interest expense section
          </p>
        </div>

        {isError && (
          <div className="err">
            Description missing - a description must be provided for every
            value.
          </div>
        )}

        <Accordion
          multiple={false}
          onClick={onClickAccordion}
          activeKeys={activeKeys}
        >
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "NGIE",
              Math.round(ngieETotal),
              tooltips.NGIE,
            )}
            itemKey="1"
          ></AccordionItem>
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Upward Adjustments",
              upwardAdjustmentsTotal,
              tooltips.upwardAdjustmentsTotal,
            )}
            itemKey="2"
          >
            {GroupDataTable(tables, [
              "capitalised_expense_in_statements",
              "capitalised_income_written_off",
              "expense_amount_recognised_in_equity_or_shareholders",
              "upward_amount_under_s330za",
              "post_2020_transitional_amounts_s607za_cta2009",
              "post_2020_income_amounts_s330za_transitional",
              "post_2020_derivative_income_s607za",
              "upward_amount_under_s330_cta_election",
              "relevant_debt_release_tax_exempt_income",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Downward Adjustments",
              downwardAdjustmentsTotal,
              tooltips.downwardAdjustmentsTotal,
            )}
            itemKey="3"
          >
            {GroupDataTable(tables, [
              "capitalised_income_in_statements",
              "capitalised_expense_written_off",
              "income_amount_recognised_in_equity_or_shareholders",
              "downward_amount_under_s330ZA",
              "post_2020_derivative_expense_s607za",
              "downward_amount_under_s330_cta_election",
              "expense_amount_excluded_for_s322_2_or_cta2009",
              "expense_amount_for_dividends_on_preference_shares",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Group Mismatch Adjustments (SI 2017/1224)",
              mismatchAdjustmentsTotal,
              tooltips.mismatchAdjustmentsTotal,
            )}
            itemKey="4"
          >
            {GroupDataTable(tables, [
              "mismatches_regulation_2_adjustments",
              "mismatches_regulation_3_adjustments",
              "mismatches_regulation_4_adjustments",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Interest Allowance (Alternative Calculation) Election",
              interestAltCalcTotal,
              tooltips.interestAltCalcTotal,
            )}
            itemKey="5"
          >
            {GroupDataTable(tables, [
              "capitalised_upward_interest_adjustment",
              "capitalised_downward_interest_adjustment",
              "reversal_gaap_tax_assets_s423_2_a",
              "gaap_assets_written_off_s423_2_b",
              "gaap_relevant_expense_written_off_s423_2a",
              "gaap_relevant_income_written_off_s423_2b",
              "gaap_asset_carrying_value_relevant_expense_s423_2aa_a",
              "gaap_asset_carrying_value_relevant_income_s423_2aa_b",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Interest Allowance (Non-Consolidated Investment) Election",
              sumArray([
                tables["angie_proportion_associated_groups_s427_4"]?.state[0],
              ]),
              tooltips.nonConsolidatedElection,
            )}
            itemKey="7"
          >
            {GroupDataTable(tables, [
              "angie_proportion_associated_groups_s427_4",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments for Qualifying Infrastructure Companies",
              infraAdjustmentsTotal,
              tooltips.adjustmentsForQualifyingCompanies,
            )}
            itemKey="8"
          >
            {GroupDataTable(tables, [
              "exclude_qic_income_exempt_s440_s442_2a",
              "exclude_qic_expense_exempt_s438_s442_2a",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Other",
              otherAdjustmentsTotal,
              tooltips.other,
            )}
            itemKey="6"
          >
            {GroupDataTable(tables, [
              "specialist_regimes_adjustments",
              "miscellaneous_adjustments",
            ])}
          </AccordionItem>
        </Accordion>

        {/* {renderDynamicTable(tableOtherAdjustmentsCalcNGIE, setTableOtherAdjustmentsCalcNGIE, setIsError)} */}

        <h3>Summary</h3>
        {renderSummary(
          currentPeriod?.currency,
          currentPeriod?.fx_rate,
          totalAdjustments,
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <DataImportModal pageName="angie" onSuccess={onImportSuccess} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              paddingTop: "30px",
              gap: 10,
            }}
          >
            <Button
              neverReadonly
              onClick={() => {
                handleCancel(
                  isModalChanged,
                  isReadOnly,
                  setVisible,
                  setShowWarningModal,
                  setIsError,
                );
              }}
              kind="secondary"
            >
              Cancel
            </Button>
            <Button
              hiddenInReadonly
              disabled={isError || isParentError}
              onClick={submit}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <WarningModal
        visible={showWarningModal}
        setIsVisible={setShowWarningModal}
        callBackFunc={handleExitWithoutSaving}
      ></WarningModal>
    </>
  );
};

export default ANGIE;
